import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const ThemeContext = createContext(null);

// Denver coordinates
const DENVER_LAT = 39.7392;
const DENVER_LNG = -104.9903;

/**
 * Calculate sunrise and sunset times for Denver using solar position math.
 * Returns { sunrise: Date, sunset: Date } for today.
 */
function getDenverSunTimes() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now - start;
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  // Solar declination
  const declination = 23.45 * Math.sin((2 * Math.PI / 365) * (dayOfYear - 81));
  const declinationRad = declination * (Math.PI / 180);
  const latRad = DENVER_LAT * (Math.PI / 180);
  
  // Hour angle
  const cosHourAngle = -(Math.tan(latRad) * Math.tan(declinationRad));
  const hourAngle = Math.acos(Math.max(-1, Math.min(1, cosHourAngle))) * (180 / Math.PI);
  
  // Sunrise and sunset in hours (UTC)
  const solarNoon = 12 - (DENVER_LNG / 15);
  const sunriseUTC = solarNoon - (hourAngle / 15);
  const sunsetUTC = solarNoon + (hourAngle / 15);
  
  // Convert to local Date objects
  const sunrise = new Date(now);
  sunrise.setUTCHours(Math.floor(sunriseUTC), Math.round((sunriseUTC % 1) * 60), 0, 0);
  
  const sunset = new Date(now);
  sunset.setUTCHours(Math.floor(sunsetUTC), Math.round((sunsetUTC % 1) * 60), 0, 0);
  
  return { sunrise, sunset };
}

function getAutoTheme() {
  const now = new Date();
  const { sunrise, sunset } = getDenverSunTimes();
  return (now >= sunrise && now < sunset) ? 'light' : 'dark';
}

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const stored = localStorage.getItem('eventism_theme_mode');
    return stored || 'dark'; // Default to dark theme
  });

  const [theme, setThemeState] = useState(() => {
    const storedMode = localStorage.getItem('eventism_theme_mode');
    if (storedMode === 'light') return 'light';
    if (storedMode === 'auto') return getAutoTheme();
    return 'dark'; // Default to dark
  });

  // Apply theme to DOM
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
  }, [theme]);

  // Auto-update theme every minute when in auto mode
  useEffect(() => {
    if (mode !== 'auto') return;
    
    const interval = setInterval(() => {
      setThemeState(getAutoTheme());
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [mode]);

  // Persist mode
  useEffect(() => {
    localStorage.setItem('eventism_theme_mode', mode);
  }, [mode]);

  const cycleTheme = useCallback(() => {
    // Cycle: auto -> light -> dark -> auto
    if (mode === 'auto') {
      setMode('light');
      setThemeState('light');
    } else if (mode === 'light') {
      setMode('dark');
      setThemeState('dark');
    } else {
      setMode('auto');
      setThemeState(getAutoTheme());
    }
  }, [mode]);

  const setTheme = useCallback((t) => {
    setMode(t);
    if (t === 'auto') {
      setThemeState(getAutoTheme());
    } else {
      setThemeState(t);
    }
  }, []);

  const sunTimes = getDenverSunTimes();

  const value = {
    theme,
    mode,
    setTheme,
    cycleTheme,
    isDark: theme === 'dark',
    sunTimes,
    toggleTheme: cycleTheme
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
